import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { listReturnStateFromWindow } from '../../hooks/useListState'
import { useTranslation } from 'react-i18next'
import {
  ChevronDown,
  Copy,
  HeartHandshake,
  Mic,
  Phone,
  PhoneIncoming,
  PhoneOff,
  PhoneOutgoing,
  UserPlus,
  UserRound,
} from 'lucide-react'
import toast from 'react-hot-toast'

import { useUnknownNumber } from '../../hooks/useUnknownNumber'
import { usePermissions } from '../../hooks/usePermissions'
import type { PhoneHistoryCall } from '../../services/phoneHistory'
import type { DonationLookup } from '../../services/allodons'
import { EMPTY_VALUE, formatCurrency, formatDate, formatDuration, formatTime } from '../../utils/format'
import { Alert } from '../ui/Alert'
import { Badge } from '../ui/Badge'
import { Button } from '../ui/Button'
import { EmptyState } from '../ui/EmptyState'
import { Skeleton } from '../ui/LoadingState'
import { Modal } from '../ui/Modal'
import { CALL_OUTCOME_KEYS, CALL_STATUS_KEYS, callStatusBadge } from '../journals/callStatus'
import CreateContactModal from '../modals/CreateContactModal'
import { RecentDonationsList } from '../donations/RecentDonationsList'
import { formatPhoneForDisplay, phoneKindOf } from './phoneDisplay'
import { toastApiError } from '../../utils/apiError'

/**
 * PANNEAU « NUMÉRO INCONNU » (LOT-NUMÉRO-INCONNU).
 *
 * Demande du client (2026-10-13) : « les numéros appelés mais non enregistrés
 * dans le CRM ont-ils un historique comme les contacts ? » Jusqu'ici, non : un
 * numéro sans fiche apparaissait dans le journal des appels, la liste « À
 * rappeler » et les enregistrements, et rien ne rassemblait son histoire.
 *
 * Ce panneau est cette surface. Il s'ouvre sur le clic d'un numéro SANS fiche
 * (journal, « À rappeler », enregistrements) et montre, pour ce numéro et ce
 * tenant :
 *   - l'en-tête : numéro formaté, insécable (`whitespace-nowrap`), le brut en
 *     `title` ; mobile / fixe quand le numéro le dit de lui-même ;
 *   - l'HISTORIQUE des appels (sens, date, statut, issue, durée, agent,
 *     enregistrement) — 50 au plus (l'endpoint ne pagine pas) ; au-delà de
 *     `HISTORY_PREVIEW_COUNT`, « Voir plus » déplie le reste côté écran ;
 *   - les DONS AlloDons — section masquée dès qu'il n'y a rien à montrer
 *     (tenant sans raccordement, zéro don, échec) : jamais une erreur visible ;
 *   - deux actions : « Copier le numéro » et « Créer la fiche contact »
 *     (le formulaire EXISTANT de la qualification, `CreateContactModal`,
 *     numéro prérempli ; gardé par `canCreateContacts`). PAS de bouton
 *     « Appeler » : la composition vit dans la scène (zone gelée).
 *
 * Si le numéro a FINALEMENT une fiche (`find-by-phone` la rend), le panneau se
 * contente d'un lien « Ouvrir la fiche » — l'histoire se lit là-bas.
 *
 * Les données se chargent AU MONTAGE du panneau seulement (`phone` non nul),
 * jamais au rendu des listes qui le déclenchent.
 */

/** Lignes d'historique montrées avant « Voir plus ». */
export const HISTORY_PREVIEW_COUNT = 10

/** Plafond de l'endpoint (`take: 50`, `calls-compat.service.ts`). */
const HISTORY_SERVER_CAP = 50

export interface UnknownNumberPanelProps {
  /** Numéro (E.164 de préférence) ; `null` = panneau fermé, rien n'est monté. */
  phone: string | null
  onClose: () => void
  /**
   * R1 — une fiche vient d'être créée pour ce numéro : le serveur a rattaché
   * ses appels passés (`Call.contactId`). La surface qui a ouvert le panneau
   * recharge son journal pour que la ligne passe au nom du contact.
   */
  onContactCreated?: () => void
  /**
   * #396 — nom que la surface d'origine affichait pour ce numéro (liste
   * « À rappeler » : fiche nommée du tenant). Il devient le TITRE du panneau
   * (le numéro reste en sous-titre : c'est l'objet du panneau). Absent ou vide :
   * « Numéro inconnu », comme avant.
   */
  contactName?: string | null
}

export function UnknownNumberPanel({
  phone,
  onClose,
  onContactCreated,
  contactName,
}: UnknownNumberPanelProps) {
  // Rien n'est monté tant que le panneau est fermé : le hook (et ses
  // requêtes) ne vit que pendant l'ouverture.
  if (!phone) return null
  return (
    <UnknownNumberDialog
      key={phone}
      phone={phone}
      onClose={onClose}
      onContactCreated={onContactCreated}
      contactName={contactName}
    />
  )
}

function UnknownNumberDialog({
  phone,
  onClose,
  onContactCreated,
  contactName,
}: {
  phone: string
  onClose: () => void
  onContactCreated?: () => void
  contactName?: string | null
}) {
  const { t } = useTranslation(['contacts', 'common', 'logs'])
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const data = useUnknownNumber(phone)
  const [showCreate, setShowCreate] = useState(false)
  const [showAll, setShowAll] = useState(false)

  const shown = formatPhoneForDisplay(phone)
  const kind = phoneKindOf(phone)
  const canCreate = hasPermission('canCreateContacts')

  const copyNumber = async () => {
    try {
      const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined
      if (!clipboard) throw new Error('clipboard unavailable')
      // Le BRUT, pas le texte formaté : c'est lui qui se colle dans un composeur.
      await clipboard.writeText(phone)
      toast.success(t('contacts:unknownNumber.actions.copied'))
    } catch (error) {
      toastApiError(error, t('contacts:unknownNumber.actions.copyFailed'))
    }
  }

  const openContact = () => {
    if (!data.contact) return
    // #259 — la fiche revient à l'écran d'ORIGINE (Enregistrements, journal,
    // rappels), et non plus à « Contacts » par défaut.
    navigate(`/contacts/${data.contact.id}/timeline`, { state: listReturnStateFromWindow() })
    onClose()
  }

  const subtitle = (
    <>
      <span title={phone} className="whitespace-nowrap tabular-nums">
        {shown}
      </span>
      {kind && <> · {t(`contacts:unknownNumber.kind.${kind}`)}</>}
    </>
  )

  const footer = (
    <>
      <Button variant="ghost" icon={Copy} onClick={() => void copyNumber()} className="flex-1">
        {t('contacts:unknownNumber.actions.copy')}
      </Button>
      {data.contact ? (
        <Button variant="primary" icon={UserRound} onClick={openContact} className="flex-1">
          {t('contacts:unknownNumber.known.open')}
        </Button>
      ) : (
        canCreate &&
        data.contact === null && (
          <Button
            variant="primary"
            icon={UserPlus}
            onClick={() => setShowCreate(true)}
            className="flex-1"
          >
            {t('contacts:unknownNumber.actions.createContact')}
          </Button>
        )
      )}
    </>
  )

  return (
    <>
      <Modal
        // #239 : le formulaire de création est lui aussi un `Modal` ; deux
        // pièges de focus (écouteurs `window`) ne cohabitent pas — le panneau
        // se met en retrait pendant qu'il est ouvert (son état et son cache
        // sont conservés : il revient tel quel à la fermeture).
        open={!showCreate}
        onClose={onClose}
        icon={Phone}
        title={contactName?.trim() || t('contacts:unknownNumber.title')}
        subtitle={subtitle}
        width="lg"
        footer={footer}
        data-testid="unknown-number-panel"
      >
        {data.contactLoading ? (
          <SectionSkeleton rows={3} />
        ) : data.contact ? (
          <Alert
            tone="info"
            icon={UserRound}
            title={t('contacts:unknownNumber.known.title', { name: data.contact.name })}
          >
            {t('contacts:unknownNumber.known.description')}
          </Alert>
        ) : (
          <div className="space-y-6">
            <section aria-labelledby="unknown-number-history">
              <h3
                id="unknown-number-history"
                className="mb-2 flex flex-wrap items-center gap-2 text-t4 font-semibold text-foreground"
              >
                {t('contacts:unknownNumber.history.title')}
                {data.history && data.history.length > 0 && (
                  <Badge>
                    {data.history.length >= HISTORY_SERVER_CAP
                      ? t('contacts:unknownNumber.history.capped', { count: HISTORY_SERVER_CAP })
                      : t('contacts:unknownNumber.history.count', { count: data.history.length })}
                  </Badge>
                )}
              </h3>
              {data.historyLoading ? (
                <SectionSkeleton rows={3} />
              ) : data.historyError ? (
                <Alert
                  tone="danger"
                  title={t('contacts:unknownNumber.history.error')}
                  action={
                    <Button variant="secondary" size="sm" onClick={data.retryHistory}>
                      {t('common:actions.retry')}
                    </Button>
                  }
                />
              ) : !data.history || data.history.length === 0 ? (
                <EmptyState
                  density="compact"
                  icon={PhoneOff}
                  title={t('contacts:unknownNumber.history.empty')}
                  description={t('contacts:unknownNumber.history.emptyHint')}
                />
              ) : (
                <HistoryList
                  calls={showAll ? data.history : data.history.slice(0, HISTORY_PREVIEW_COUNT)}
                  hiddenCount={showAll ? 0 : Math.max(0, data.history.length - HISTORY_PREVIEW_COUNT)}
                  onShowAll={() => setShowAll(true)}
                />
              )}
            </section>

            {!data.donationsHidden && data.donations && <DonationsSection lookup={data.donations} />}
          </div>
        )}
      </Modal>

      {/* Le formulaire EXISTANT de la qualification (numéro prérempli), qui prend
          la place de la modale pendant son ouverture. Au succès, la fiche est relue : le panneau
          bascule sur « Ouvrir la fiche ». */}
      {canCreate && (
        <CreateContactModal
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
          initialPhone={phone}
          onCreated={() => {
            data.invalidateContact()
            onContactCreated?.()
          }}
        />
      )}
    </>
  )
}

/** Squelette aux cotes d'une ligne d'historique — jamais un « 0 », jamais un spinner. */
function SectionSkeleton({ rows }: { rows: number }) {
  const { t } = useTranslation('common')
  return (
    <div role="status" aria-busy="true" aria-label={t('states.loading')} className="space-y-3">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex items-center gap-3">
          <Skeleton className="h-4 w-4 shrink-0 rounded-full" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-5 w-20 rounded-pill" />
          <Skeleton className="h-4 flex-1" />
        </div>
      ))}
    </div>
  )
}

function HistoryList({
  calls,
  hiddenCount,
  onShowAll,
}: {
  calls: PhoneHistoryCall[]
  hiddenCount: number
  onShowAll: () => void
}) {
  const { t } = useTranslation(['contacts', 'logs'])
  return (
    <>
      <ul className="divide-y divide-border">
        {calls.map((call) => {
          const inbound = call.direction === 'INBOUND'
          const DirectionIcon = inbound ? PhoneIncoming : PhoneOutgoing
          const directionLabel = inbound
            ? t('logs:calls.direction.inbound')
            : t('logs:calls.direction.outbound')
          const badge = callStatusBadge(call.status)
          const statusKey = CALL_STATUS_KEYS[call.status]
          const statusLabel = statusKey ? t(`contacts:callStatus.${statusKey}`) : call.status
          const outcomeKey =
            call.outcome && call.outcome !== 'PENDING' ? CALL_OUTCOME_KEYS[call.outcome] : undefined
          // R3 (recette) : « Sans réponse Sans réponse » — quand l'issue porte
          // le MÊME libellé que le statut, le statut suffit.
          const outcomeLabel = outcomeKey ? t(`contacts:outcome.${outcomeKey}`) : null
          const showOutcome = outcomeLabel !== null && outcomeLabel !== statusLabel
          const when = call.startedAt || call.createdAt
          return (
            <li key={call.id} className="flex min-h-touch flex-wrap items-center gap-x-3 gap-y-1 py-2">
              {/* La DIRECTION est un fait : forme de l'icône, jamais couleur (§1.2). */}
              <span className="inline-flex shrink-0 items-center text-foreground-secondary" title={directionLabel}>
                <DirectionIcon className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">{directionLabel}</span>
              </span>
              <span className="shrink-0 tabular-nums text-t5 text-foreground-secondary">
                {formatDate(when)} {formatTime(when)}
              </span>
              <Badge tone={badge.tone} icon={badge.icon}>
                {statusLabel}
              </Badge>
              {showOutcome && <span className="text-t5 text-foreground">{outcomeLabel}</span>}
              <span className="tabular-nums text-t5 text-foreground-secondary">
                {formatDuration(call.duration || 0)}
              </span>
              <span className="min-w-0 flex-1 truncate text-t5 text-foreground-secondary">
                {call.user?.name || call.user?.email?.split('@')[0] || EMPTY_VALUE}
              </span>
              {call.hasRecording && (
                <span
                  className="inline-flex shrink-0 items-center text-foreground-secondary"
                  title={t('contacts:unknownNumber.history.recorded')}
                >
                  <Mic className="h-4 w-4" aria-hidden="true" />
                  <span className="sr-only">{t('contacts:unknownNumber.history.recorded')}</span>
                </span>
              )}
            </li>
          )
        })}
      </ul>
      {hiddenCount > 0 && (
        <div className="mt-1 flex justify-center border-t border-border pt-1">
          <Button variant="ghost" size="sm" icon={ChevronDown} onClick={onShowAll}>
            {t('contacts:unknownNumber.history.showMore', { count: hiddenCount })}
          </Button>
        </div>
      )}
    </>
  )
}

function DonationsSection({ lookup }: { lookup: DonationLookup }) {
  const { t } = useTranslation('contacts')
  const { summary, items, total } = lookup
  const remaining = Math.max(0, total - items.length)
  return (
    <section aria-labelledby="unknown-number-donations">
      <h3
        id="unknown-number-donations"
        className="mb-2 flex items-center gap-2 text-t4 font-semibold text-foreground"
      >
        <HeartHandshake className="h-4 w-4 shrink-0 text-foreground-secondary" aria-hidden="true" />
        {t('unknownNumber.donations.title')}
      </h3>
      <p className="text-t5 text-foreground">
        {t('unknownNumber.donations.summary', {
          count: summary.donationCount,
          total: formatCurrency(summary.totalEur, 'EUR'),
        })}
        {summary.lastDonationDate && (
          <span className="text-foreground-secondary">
            {' · '}
            {t('unknownNumber.donations.last', { date: formatDate(summary.lastDonationDate) })}
          </span>
        )}
      </p>
      {/* Lignes de dons : rendu PARTAGÉ avec la fiche contact et l'onglet Dons
          de la scène (LOT-DONS-AGENT-1). */}
      <RecentDonationsList items={items} className="mt-2" />
      {remaining > 0 && (
        <p className="mt-1 text-t6 text-foreground-secondary">
          {t('unknownNumber.donations.more', { count: remaining })}
        </p>
      )}
    </section>
  )
}

export default UnknownNumberPanel
